import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// types.
import { CanfarServiceType, ComputeServiceType, Site } from '../utils/types';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

export interface GlobalState {
  loggedIn: boolean | undefined;
  sessionValid: boolean | undefined;
  loggedInRefreshTrigger: number | undefined;
  username: string | undefined;
  preferredUsername: string | undefined;
  siteCapabilitiesTokenObtained: boolean | undefined;
  dataManagementTokenObtained: boolean | undefined;
  gatewayBackendToken: string | undefined;
  site: Site | undefined;
  computeServicesLoaded: boolean;
  selectedCanfar: CanfarServiceType | undefined;
  project:
    | {
        id: number;
        name?: string;
      }
    | undefined;
  darkMode: boolean;
  loadedNotifications: boolean;
  topMenuPosition: {
    leftUpper: number;
    widthUpper: number;
    leftLower: number;
    widthLower: number;
    offsetLeftLower: number;
  };
  submittingJob: boolean;
}

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

const initialState: GlobalState = {
  loggedIn: JSON.parse(sessionStorage.getItem('appState') ?? '{}').loggedIn || false,
  sessionValid: JSON.parse(sessionStorage.getItem('appState') ?? '{}').sessionValid,
  loggedInRefreshTrigger: JSON.parse(sessionStorage.getItem('appState') ?? '{}')
    .loggedInRefreshTrigger,
  username: JSON.parse(sessionStorage.getItem('appState') ?? '{}').username,
  preferredUsername: JSON.parse(sessionStorage.getItem('appState') ?? '{}')
    .preferredUsername,
  siteCapabilitiesTokenObtained: JSON.parse(sessionStorage.getItem('appState') ?? '{}')
    .siteCapabilitiesTokenObtained,
  dataManagementTokenObtained: JSON.parse(sessionStorage.getItem('appState') ?? '{}')
    .dataManagementTokenObtained,
  gatewayBackendToken: undefined,
  site: undefined,
  computeServicesLoaded: false,
  selectedCanfar: undefined,
  project: undefined,
  darkMode: JSON.parse(sessionStorage.getItem('appState') ?? '{}').darkMode,
  loadedNotifications: false,
  topMenuPosition: {
    leftUpper: 0,
    widthUpper: 0,
    leftLower: 0,
    widthLower: 0,
    offsetLeftLower: 0,
  },
  submittingJob: false,
};

//	--------------------------------------------------------------------------
//
//	S L I C E S
//
//	--------------------------------------------------------------------------

export const globalSlice = createSlice({
  name: 'global',
  initialState: initialState,
  reducers: {
    //
    rG_setComputeServicesLoaded: (state: GlobalState, action: PayloadAction<boolean>) => {
      state.computeServicesLoaded = action.payload;
    },

    rG_setDarkMode: (state: GlobalState, action: PayloadAction<boolean>) => {
      state.darkMode = action.payload;
    },

    rG_setDataManagementTokenObtained: (
      state: GlobalState,
      action: PayloadAction<boolean | undefined>,
    ) => {
      state.dataManagementTokenObtained = action.payload;
    },

    rG_setGatewayBackendToken: (
      state: GlobalState,
      action: PayloadAction<string | undefined>,
    ) => {
      state.gatewayBackendToken = action.payload;
    },

    rG_setLoadedNotifications: (state: GlobalState, action: PayloadAction<boolean>) => {
      state.loadedNotifications = action.payload;
    },

    rG_setLoggedIn: (state: GlobalState, action: PayloadAction<boolean | undefined>) => {
      state.loggedIn = action.payload;
    },

    rG_setLoggedInRefreshTrigger: (
      state: GlobalState,
      action: PayloadAction<number | undefined>,
    ) => {
      state.loggedInRefreshTrigger = action.payload;
    },

    rG_setPreferredUsername: (
      state: GlobalState,
      action: PayloadAction<string | undefined>,
    ) => {
      state.preferredUsername = action.payload;
    },

    rG_setProject: (
      state: GlobalState,
      action: PayloadAction<
        | {
            id: number;
            name?: string;
          }
        | undefined
      >,
    ) => {
      state.project = action.payload;
      if (action.payload?.id !== undefined) {
        sessionStorage.setItem('project_id', JSON.stringify({ id: action.payload.id }));
      } else {
        sessionStorage.removeItem('project_id');
      }
    },

    rG_setSelectedCanfar: (
      state: GlobalState,
      action: PayloadAction<CanfarServiceType | undefined>,
    ) => {
      state.selectedCanfar = action.payload;
    },

    rG_setSessionValid: (
      state: GlobalState,
      action: PayloadAction<boolean | undefined>,
    ) => {
      state.sessionValid = action.payload;
    },

    rG_setSite: (state: GlobalState, action: PayloadAction<Site>) => {
      state.site = action.payload;
      if (action.payload.site_uuid !== undefined) {
        sessionStorage.setItem('site_uuid', action.payload.site_uuid);
      } else {
        sessionStorage.removeItem('site_uuid');
      }
    },

    rG_setSiteCapabilitiesTokenObtained: (
      state: GlobalState,
      action: PayloadAction<boolean | undefined>,
    ) => {
      state.siteCapabilitiesTokenObtained = action.payload;
    },

    rG_setSubmittingJob: (state: GlobalState, action: PayloadAction<boolean>) => {
      state.submittingJob = action.payload;
    },

    rG_setTopMenuPosition: (
      state: GlobalState,
      action: PayloadAction<{
        leftUpper?: number;
        widthUpper?: number;
        widthLower?: number;
        offsetLeftLower?: number;
      }>,
    ) => {
      //
      if (action.payload.leftUpper !== undefined) {
        state.topMenuPosition.leftUpper = action.payload.leftUpper;
      }
      if (action.payload.widthUpper !== undefined) {
        state.topMenuPosition.widthUpper = action.payload.widthUpper;
      }
      if (action.payload.widthLower !== undefined) {
        state.topMenuPosition.widthLower = action.payload.widthLower;
      }
      if (action.payload.offsetLeftLower !== undefined) {
        state.topMenuPosition.offsetLeftLower = action.payload.offsetLeftLower;
      }
      state.topMenuPosition.leftLower =
        state.topMenuPosition.leftUpper -
        state.topMenuPosition.offsetLeftLower +
        (state.topMenuPosition.widthUpper - state.topMenuPosition.widthLower) / 2;
      //
    },

    rG_setUsername: (state: GlobalState, action: PayloadAction<string | undefined>) => {
      state.username = action.payload;
    },
    //
  },
});

// expose the interface.
export const {
  rG_setLoggedIn,
  rG_setSessionValid,
  rG_setLoggedInRefreshTrigger,
  rG_setSiteCapabilitiesTokenObtained,
  rG_setDataManagementTokenObtained,
  rG_setGatewayBackendToken,
  rG_setSite,
  rG_setComputeServicesLoaded,
  rG_setSelectedCanfar,
  rG_setProject,
  rG_setUsername,
  rG_setPreferredUsername,
  rG_setDarkMode,
  rG_setSubmittingJob,
  rG_setTopMenuPosition,
} = globalSlice.actions;

export default globalSlice.reducer;
